import { OFFER_STATUS_META } from 'dashboard/constants/lais/offer';
import { formatBRL } from 'dashboard/helper/lais/currency';

export { OFFER_STATUS_META };

export const STAGE_ORDER = [
  'sem_visita',
  'uma_ou_duas_visitas',
  'tres_ou_mais_visitas',
  'proposta_em_andamento',
  'negocio_fechado',
];

export const ATTENDANCE_STAGE_ENABLED = Object.freeze({
  sem_visita: STAGE_ORDER,
  uma_ou_duas_visitas: STAGE_ORDER.slice(1),
  tres_ou_mais_visitas: STAGE_ORDER.slice(2),
  proposta_em_andamento: STAGE_ORDER.slice(3),
  negocio_fechado: STAGE_ORDER.slice(4),
});

export const enabledAttendanceStagesFrom = stage =>
  ATTENDANCE_STAGE_ENABLED[stage] || STAGE_ORDER;

export const STAGE_META = {
  sem_visita: {
    labelKey: 'LEADS.PIPELINE.STAGES.NO_VISIT',
    descriptionKey: 'LEADS.PIPELINE.STAGE_DESCRIPTIONS.NO_VISIT',
    variant: 'new',
    colorClass: 'bg-lais-primary',
  },
  uma_ou_duas_visitas: {
    labelKey: 'LEADS.PIPELINE.STAGES.ONE_TWO_VISITS',
    descriptionKey: 'LEADS.PIPELINE.STAGE_DESCRIPTIONS.ONE_TWO_VISITS',
    variant: 'scheduled',
    colorClass: 'bg-lais-info',
  },
  tres_ou_mais_visitas: {
    labelKey: 'LEADS.PIPELINE.STAGES.THREE_PLUS_VISITS',
    descriptionKey: 'LEADS.PIPELINE.STAGE_DESCRIPTIONS.THREE_PLUS_VISITS',
    variant: 'contacted',
    colorClass: 'bg-lais-primary-400',
  },
  proposta_em_andamento: {
    labelKey: 'LEADS.PIPELINE.STAGES.OFFER',
    descriptionKey: 'LEADS.PIPELINE.STAGE_DESCRIPTIONS.OFFER',
    variant: 'offer',
    colorClass: 'bg-lais-warning',
  },
  negocio_fechado: {
    labelKey: 'LEADS.PIPELINE.STAGES.CLOSED',
    descriptionKey: 'LEADS.PIPELINE.STAGE_DESCRIPTIONS.CLOSED',
    variant: 'won',
    colorClass: 'bg-lais-success',
  },
};

export const REFERRAL_STATUS_META = {
  pending: {
    labelKey: 'LEADS.REFERRALS.STATUS.PENDING',
    variant: 'attention',
  },
  confirmed: {
    labelKey: 'LEADS.REFERRALS.STATUS.CONFIRMED',
    variant: 'success',
  },
  declined: {
    labelKey: 'LEADS.REFERRALS.STATUS.DECLINED',
    variant: 'error',
  },
  returned: {
    labelKey: 'LEADS.REFERRALS.STATUS.RETURNED',
    variant: 'neutral',
  },
  expired: {
    labelKey: 'LEADS.REFERRALS.STATUS.EXPIRED',
    variant: 'neutral',
  },
  inactive: {
    labelKey: 'LEADS.REFERRALS.STATUS.INACTIVE',
    variant: 'error',
  },
  not_eligible: {
    labelKey: 'LEADS.REFERRALS.STATUS.NOT_ELIGIBLE',
    variant: 'error',
  },
  invalid: {
    labelKey: 'LEADS.REFERRALS.STATUS.INVALID',
    variant: 'error',
  },
};

export const toNumber = value => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export const compactObject = value =>
  Object.fromEntries(
    Object.entries(value || {}).filter(
      ([, item]) => item != null && item !== ''
    )
  );

export const queryString = params =>
  new URLSearchParams(compactObject(params)).toString();

export const leadDisplayName = lead => lead?.name || lead?.contact?.name || '';

export const leadInitials = lead => {
  const name = leadDisplayName(lead).trim();
  if (!name) return '?';

  return name
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();
};

export const leadContactLine = lead =>
  lead?.phone_number ||
  lead?.email ||
  lead?.contact?.phone_number ||
  lead?.contact?.email ||
  '';

export const leadStats = lead => {
  const crm = lead?.lais_crm || lead?.additional_attributes?.lais_crm || {};
  const stats = crm.stats || crm.activity_stats || {};

  return {
    visits: toNumber(
      lead?.finished_visits_count ?? stats.finished_visits_count
    ),
    offers: toNumber(lead?.active_offers_count ?? stats.active_offers_count),
    lastActivity:
      lead?.last_activity_at || crm.last_activity_at || lead?.updated_at,
  };
};

export const resolveLeadStage = lead => {
  const explicitStage = lead?.attendance_stage;
  if (STAGE_META[explicitStage]) return explicitStage;

  const stats = leadStats(lead);
  if (lead?.qualification_status === 'pronto' || stats.offers > 0) {
    return 'proposta_em_andamento';
  }
  if (stats.visits >= 3) return 'tres_ou_mais_visitas';
  if (stats.visits >= 1) return 'uma_ou_duas_visitas';

  return 'sem_visita';
};

export const buildStageBuckets = leads => {
  const total = Math.max(leads.length, 1);
  const buckets = STAGE_ORDER.map(key => ({
    key,
    ...STAGE_META[key],
    leads: [],
    count: 0,
    percent: 0,
  }));

  const byKey = Object.fromEntries(buckets.map(bucket => [bucket.key, bucket]));

  leads.forEach(lead => {
    const bucket = byKey[resolveLeadStage(lead)] || byKey.sem_visita;
    bucket.leads.push(lead);
    bucket.count += 1;
  });

  return buckets.map(bucket => ({
    ...bucket,
    percent: Math.round((bucket.count / total) * 100),
  }));
};

export const resolveBuyerProfileKind = (profile, businessContext = '') => {
  if (!profile) return '';
  const normalizedContext = String(businessContext || '').toLowerCase();
  const isSaleContext = ['for_sale', 'sale', 'venda', 'purchase'].includes(
    normalizedContext
  );
  if (
    isSaleContext &&
    (profile.intent === 'investor' || profile.investidor === true)
  ) {
    return 'investor';
  }
  if (profile.intent === 'home') return 'home';
  if (
    profile.budget_min ||
    profile.budget_max ||
    profile.faixa_preco_min ||
    profile.faixa_preco_max ||
    profile.regions ||
    profile.regioes
  ) {
    return 'profile';
  }

  return '';
};

export const formatDate = (value, locale = 'pt-BR') => {
  if (!value) return '';
  const numeric = Number(value);
  const date = new Date(
    Number.isFinite(numeric) && numeric < 1_000_000_000_000
      ? numeric * 1000
      : value
  );

  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(locale);
};

export const formatCurrency = (value, locale = 'pt-BR', currency = 'BRL') => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';

  if (locale === 'pt-BR' && currency === 'BRL') {
    return formatBRL(number, { fallback: '' }).replace(/\u00a0/g, ' ');
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  })
    .format(number)
    .replace(/\u00a0/g, ' ');
};
